import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import axios from 'axios';
import { useUserStore } from '../../store';
import { getCsrfToken } from '../../utils';

const BillingSettingsDialog = ({ open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();

  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setIban('');
      setBic('');
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    const trimmedIban = iban.trim();
    if (!trimmedIban) {
      setError(t('IBAN is required'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await axios.patch(
        `/api/organizations/${currentOrganization.organization_id}/`,
        { iban: trimmedIban, bic: bic.trim() },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
            'X-Organization-Id': currentOrganization.organization_id,
          },
        }
      );

      onSaved();
    } catch (err) {
      const message = err.response?.data?.detail || t('Failed to save billing settings');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon color="primary" />
        {t('Billing settings required')}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('billingSettingsDialogDescription')}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('IBAN')}
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            placeholder="FI00 0000 0000 0000 00"
            required
            fullWidth
            autoFocus
          />
          <TextField
            label={t('BIC/SWIFT')}
            value={bic}
            onChange={(e) => setBic(e.target.value)}
            placeholder="NDEAFIHH"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !iban.trim()}
          startIcon={saving ? <CircularProgress size={20} /> : <AccountBalanceIcon />}
        >
          {saving ? t('Saving...') : t('Save and continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingSettingsDialog;
